import { createSelector } from 'reselect';

import { NEW_ENTITY_ID } from 'constants/EntityTitles';
import { getDefaultLanguage } from 'modules/checklists/selectors/details';
import { getLocale } from 'modules/i18n/selectors/data';
import { getDealerDefaultLanguage, getSelectedDealerId } from 'selectors/dealerSelectors';

import { getUI, getById, getRoot } from '.';

/** @typedef {import('../types').PredefinedMessage} PredefinedMessage */

/** @type {function():any} */
const getProps = (_, props) => props;

/** @return {function():import('../types').State} */
const getDetails = createSelector(getUI, ui => ui.details);

/** @type {function():string}  */
export const getSelectedId = createSelector(getDetails, details => details.selectedId);

/** @type {function():Object<string, PredefinedMessage>}  */
const getSelectedPredefinedMessage = createSelector(getRoot, getSelectedId, getById);

/** @type {function():boolean}  */
export const isNew = createSelector(getSelectedId, id => id === NEW_ENTITY_ID);

/** @type {function():PredefinedMessage}  */
const getNewPredefinedMessage = createSelector(getDealerDefaultLanguage, getSelectedDealerId, (language, dealerId) => ({
  title: { [language]: '' },
  content: { [language]: '' },
  dealerId,
}));

/** @type {function():PredefinedMessage}  */
export const getDetailPredefinedMessage = createSelector(
  getSelectedPredefinedMessage,
  getNewPredefinedMessage,
  isNew,
  (predefinedMessage, newPredefinedMessage, isNewPredefinedMessage) => ({
    ...(isNewPredefinedMessage ? newPredefinedMessage : {}),
    ...predefinedMessage,
  })
);

/** @type {function():boolean}  */
export const isLoading = createSelector(getDetails, details => details.isLoading);

/** @type {function():string}  */
const getError = createSelector(getDetails, details => details.apiError);

/** @type {function():boolean}  */
export const hasError = createSelector(getError, error => !!error);

/** @type {function():string}  */
export const getSelectedLanguage = createSelector(getDetails, details => details.selectedLanguage);

/** @type {function():Array<string>}  */
export const getLanguages = createSelector(getDetails, details => details.languages);

/** @type {function():boolean}  */
export const isReady = createSelector(getDetails, details => details.isReady);

/** @type {function():boolean}  */
export const isUpdating = createSelector(getDetails, details => details.isUpdating);

/** @type {function():string} */
export const getFieldPlaceholder = createSelector(
  getLocale,
  getDefaultLanguage,
  getProps,
  (appLocale, defaultLocale, item = {}) => {
    const languages = Object.keys(item);
    return item[appLocale] || item[defaultLocale] || item[languages[0]];
  }
);
